import * as React from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  ClipboardList,
  Clock,
  Download,
  FlaskConical,
  Info,
  Loader2,
  RefreshCw,
  ScanSearch,
  Sparkles,
  Stethoscope,
  TrendingUp,
  Users,
  Image as ImageIcon,
  Video,
  Eye,
  Heart,
  Share2,
  Repeat,
  FileText,
  MessageSquare,
  Bookmark,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { SixDimRadar } from '@/components/six-dim-radar';
import {
  diagnosisQuote,
  diagnosisCreateRun,
  diagnosisGetRun,
  diagnosisGetReport,
  diagnosisMyRuns,
  diagnosisRetryRun,
  diagnosisGetOutcomes,
  diagnosisAddOutcome,
  type DiagnosisPlatform,
  type DiagnosisRun,
  type DiagnosisQuoteResponse,
} from '@/api/client';

// ─── 平台配置 ───────────────────────────────────────────────────

const PLATFORM_INFO: Record<string, { label: string; dot: string; textCls: string; badgeCls: string }> = {
  xiaohongshu: {
    label: '小红书', dot: 'bg-pink-500', textCls: 'text-pink-600 dark:text-pink-400',
    badgeCls: 'bg-pink-500/10 text-pink-600 dark:text-pink-400',
  },
  douyin: {
    label: '抖音', dot: 'bg-sky-500', textCls: 'text-sky-600 dark:text-sky-400',
    badgeCls: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
  },
  wechat: {
    label: '视频号', dot: 'bg-emerald-500', textCls: 'text-emerald-600 dark:text-emerald-400',
    badgeCls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  },
};

const PLATFORMS: { value: DiagnosisPlatform; label: string; tagline: string; covers: string[]; hint: string; goals: { value: string; label: string }[]; steps: string[] }[] = [
  {
    value: 'xiaohongshu',
    label: '小红书',
    tagline: '笔记种草 · 搜索获客 · 蒲公英投放',
    covers: ['账号资料', '笔记采集', '评论分析', '蒲公英探测'],
    hint: '粘贴小红书主页链接或小红书号（如 jia476228296）',
    goals: [
      { value: 'exposure', label: '曝光增长' },
      { value: 'search', label: '搜索获客' },
      { value: 'trust', label: '个人信任' },
      { value: 'consult', label: '咨询获客' },
      { value: 'ecommerce', label: '电商成交' },
      { value: 'brand', label: '品牌种草' },
    ],
    steps: ['账号资料', '笔记采集', '评论分析', '蒲公英探测', '报告生成'],
  },
  {
    value: 'douyin',
    label: '抖音',
    tagline: '短视频 · 直播带货 · 电商成交',
    covers: ['账号资料', '作品采集', '播放补真', '评论分析'],
    hint: '粘贴抖音主页链接、短链或 sec_uid',
    goals: [
      { value: 'exposure', label: '曝光增长' },
      { value: 'trust', label: '个人信任' },
      { value: 'consult', label: '咨询获客' },
      { value: 'live', label: '直播获客' },
      { value: 'ecommerce', label: '电商成交' },
    ],
    steps: ['账号资料', '作品采集', '播放补真', '评论分析', '报告生成'],
  },
  {
    value: 'wechat',
    label: '视频号',
    tagline: '微信生态 · 私域加微 · 公众号协同',
    covers: ['账号与认证', '作品', '评论', '合集/直播', '转录'],
    hint: '粘贴视频号作品分享链接、sph ID 或 finder username',
    goals: [
      { value: 'exposure', label: '曝光增长' },
      { value: 'trust', label: '个人信任' },
      { value: 'consult', label: '咨询获客' },
      { value: 'live', label: '直播获客' },
      { value: 'private_domain', label: '私域加微' },
      { value: 'official_account', label: '公众号协同' },
    ],
    steps: ['账号与认证', '作品', '评论', '合集/直播', '搜索', '转录', '分析', '报告'],
  },
];

const TIER_META: Record<string, { color: string; badge: string }> = {
  basic: { color: 'text-sky-600 dark:text-sky-400', badge: 'bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20' },
  enhanced: { color: 'text-violet-600 dark:text-violet-400', badge: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20' },
  deep: { color: 'text-amber-600 dark:text-amber-400', badge: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' },
};

const PLATFORM_LABEL: Record<string, string> = { xiaohongshu: '小红书', douyin: '抖音', wechat: '视频号' };

// ─── 流程向导步骤（选择平台 → 账号信息 → 确认报价 → 采集与报告） ─────
const WIZARD_STEPS = [
  { key: 1, label: '选择平台' },
  { key: 2, label: '账号信息' },
  { key: 3, label: '确认报价' },
  { key: 4, label: '采集与报告' },
];

function formatDate(s: string | null | undefined): string {
  if (!s) return '—';
  return s.replace('T', ' ').slice(5, 16);
}

// 数字格式化：0/缺失显示 ——（潇总偏好）
function fmt(n: number | null | undefined): string {
  if (n == null || n <= 0) return '—';
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function fmtDuration(d: number | null | undefined): string {
  if (!d || d <= 0) return '';
  let s = d;
  if (s > 10000) s = Math.floor(s / 1000); // 毫秒防御
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

function fmtTime(ts: number | null | undefined): string {
  if (!ts || ts <= 0) return '—';
  const d = new Date(ts * 1000);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  pending: { label: '待启动', color: 'bg-muted text-muted-foreground' },
  quoted: { label: '已报价', color: 'bg-muted text-muted-foreground' },
  queued: { label: '排队中', color: 'bg-sky-500/10 text-sky-600 dark:text-sky-400' },
  collecting: { label: '采集中', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  analyzing: { label: '分析中', color: 'bg-violet-500/10 text-violet-600 dark:text-violet-400' },
  completed: { label: '已完成', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  partial: { label: '部分完成', color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400' },
  failed: { label: '失败', color: 'bg-red-500/10 text-red-600 dark:text-red-400' },
  settled: { label: '已结算', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  refunded: { label: '已退款', color: 'bg-muted text-muted-foreground' },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_LABEL[status] || { label: status, color: 'bg-muted text-muted-foreground' };
  return <Badge variant="outline" className={cfg.color}>{cfg.label}</Badge>;
}

function DimensionCard({ dim }: { dim: any }) {
  const sevColor = (() => {
    if (!dim.score) return 'bg-muted text-muted-foreground';
    if (dim.score >= 70) return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400';
    if (dim.score >= 55) return 'bg-amber-500/10 text-amber-600 dark:text-amber-400';
    return 'bg-red-500/10 text-red-600 dark:text-red-400';
  })();
  return (
    <div className="rounded-lg border p-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">{dim.name}</div>
        <Badge variant="outline" className={sevColor}>{dim.status}</Badge>
      </div>
      <div className="mt-2 text-2xl font-bold">{dim.score != null ? `${dim.score}` : '—'}</div>
      <p className="mt-1 text-xs text-muted-foreground">{dim.note}</p>
      {dim.topics && dim.topics.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {dim.topics.slice(0, 3).map((t: any, i: number) => (
            <Badge key={i} variant="secondary" className="text-xs">{t.word} ×{t.count}</Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── 账号信息卡（人设页版式：圆头像 + 昵称 + 指标卡） ──────────
function buildProfileFields(profile: any): { label: string; value: any }[] {
  const fieldAliases: Record<string, string> = {
    nickname: '昵称', username: '用户名', red_id: '小红书号', sec_uid: '抖音号',
    platform_id: '平台ID', signature: '简介', desc: '简介', gender: '性别',
    country: '国家', province: '省份', city: '城市', ip_location: 'IP属地',
    followers: '粉丝数', following: '关注数', aweme_count: '作品数', feeds_count: '作品数',
    note_count: '作品数', total_favorited: '获赞数', total_liked: '获赞数',
    total_collected: '获收藏数', total_collect: '获收藏数',
    is_verified: '认证', verified_reason: '认证说明', custom_verify: '认证',
    association: '关联账号', associated_account: '关联公众号',
    mplatform_followers_count: '全平台粉丝', live_commerce: '直播电商',
    with_fusion_shop_entry: '个人橱窗',
  };
  const omitKeys = new Set(['avatar', 'avatar_url', 'avatar_thumb', 'avatar_larger', 'platform', 'platform_id']);
  const fields: { label: string; value: any }[] = [];
  const seen = new Set<string>();
  for (const [k, v] of Object.entries(profile || {})) {
    if (omitKeys.has(k)) continue;
    const label = fieldAliases[k] || k;
    if (v === null || v === undefined || v === '') continue;
    if (typeof v === 'object') continue;
    if (seen.has(label)) continue;
    seen.add(label);
    fields.push({ label, value: v });
  }
  return fields;
}

function AccountInfoCard({ profile, platform }: { profile: any; platform: string }) {
  const avatarUrl = profile?.avatar_url || profile?.avatar || profile?.avatar_larger || profile?.avatar_thumb || '';
  const fields = buildProfileFields(profile);
  // 关键数据指标（人设页同款指标卡）——只在有值时才展示
  const metrics = [
    { label: '作品数', value: profile?.aweme_count ?? profile?.feeds_count ?? profile?.note_count },
    { label: '粉丝数', value: profile?.followers },
    { label: '获赞数', value: profile?.total_favorited ?? profile?.total_liked },
    { label: '获收藏数', value: profile?.total_collected ?? profile?.total_collect },
    { label: '关注数', value: profile?.following },
  ].filter((m) => typeof m.value === 'number' && m.value > 0);

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start gap-5">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={profile?.nickname || '头像'}
              className="h-20 w-20 shrink-0 rounded-full border object-cover"
              referrerPolicy="no-referrer"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          ) : (
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-primary/10 text-2xl font-bold text-primary">
              {profile?.nickname?.[0] || '?'}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xl font-semibold">{profile?.nickname || '未获取到昵称'}</span>
              <PlatformDot p={platform || ''} />
              <span className="text-xs text-muted-foreground">{PLATFORM_LABEL[platform] || platform}</span>
            </div>
            {profile?.signature && (
              <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{profile.signature}</p>
            )}
            {metrics.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-3">
                {metrics.map((m) => (
                  <div key={m.label} className="rounded-lg border px-4 py-2 text-center">
                    <div className="text-lg font-bold leading-tight">{fmt(m.value)}</div>
                    <div className="text-xs text-muted-foreground">{m.label}</div>
                  </div>
                ))}
              </div>
            )}
            {fields.length > 0 && (
              <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm sm:grid-cols-3">
                {fields.map((f) => (
                  <div key={f.label} className="flex items-baseline gap-1.5">
                    <span className="shrink-0 text-xs text-muted-foreground">{f.label}</span>
                    <span className="truncate font-medium" title={String(f.value)}>{String(f.value)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── 作品网格（内容库同款卡片 + 指标展示，2026-09-12 与内容库对齐） ──────
function WorksGrid({ contents, platform }: { contents: any[]; platform?: string }) {
  const works = (contents || []).map((c: any, i: number) => {
    const cid = c.content_id || c.id || c.note_id || '';
    const mediaType = c.media_type === 'video' ? 'video' : 'image';
    return {
      key: cid || `w-${i}`,
      cid,
      title: c.title || c.desc || '无标题',
      desc: c.desc || '',
      // 体检数据 cover 常为空，用 image_list 首图兜底，避免作品卡片无封面
      cover: c.cover_url || c.cover || (Array.isArray(c.image_list) ? c.image_list[0] : '') || '',
      mediaType,
      duration: c.duration || 0,
      createTime: c.create_time || 0,
      like: c.like_count ?? c.digg_count ?? 0,
      collect: c.collect_count ?? 0,
      comment: c.comment_count ?? 0,
      share: c.share_count ?? c.forward_count ?? 0,
      read: c.read_count ?? c.play_count ?? 0,
    };
  });
  if (works.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">本次未采集到作品数据。</p>;
  }
  // 指标与内容库 platformStats 保持一致：小红书=点赞/收藏/评论/分享，视频号=点赞/评论/转发，抖音=播放/点赞/评论
  const platformKey = platform || '';
  const statsOf = (w: any) => {
    if (platformKey === 'xiaohongshu') {
      return [
        { icon: Heart, label: '点赞', value: w.like },
        { icon: Bookmark, label: '收藏', value: w.collect },
        { icon: MessageSquare, label: '评论', value: w.comment },
        { icon: Share2, label: '分享', value: w.share },
      ];
    }
    if (platformKey === 'wechat' || platformKey === 'finder') {
      return [
        { icon: Heart, label: '点赞', value: w.like },
        { icon: MessageSquare, label: '评论', value: w.comment },
        { icon: Repeat, label: '转发', value: w.share },
      ];
    }
    return [
      { icon: Eye, label: '播放', value: w.read },
      { icon: Heart, label: '点赞', value: w.like },
      { icon: MessageSquare, label: '评论', value: w.comment },
    ];
  };
  return (
    <div className="grid gap-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8">
      {works.map((w: any) => (
        <div key={w.key} className="group overflow-hidden rounded-md border bg-background transition-all hover:shadow-md">
          <div className="relative overflow-hidden bg-muted" style={{ aspectRatio: '3/4' }}>
            {w.cover ? (
              <img src={w.cover} alt="" referrerPolicy="no-referrer" className="h-full w-full object-cover transition-transform group-hover:scale-105" />
            ) : (
              <div className="flex h-full items-center justify-center text-muted-foreground/50">
                {w.mediaType === 'video' ? <Video className="h-5 w-5" /> : <ImageIcon className="h-5 w-5" />}
              </div>
            )}
            {w.mediaType === 'video' && w.duration ? (
              <div className="absolute bottom-1 right-1 rounded bg-black/70 px-1 py-0.5 text-[9px] text-white">{fmtDuration(w.duration)}</div>
            ) : null}
          </div>
          <div className="space-y-1 p-2">
            <div className="line-clamp-1 text-xs font-medium leading-snug">{w.title}</div>
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
              {statsOf(w).map((s, i) => (
                <span key={i} className="inline-flex items-center gap-0.5" title={s.label}><s.icon className="h-2.5 w-2.5" />{fmt(s.value)}</span>
              ))}
            </div>
            <div className="text-[10px] text-muted-foreground">{fmtTime(w.createTime)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── 报告分页：步骤定义（诊断报告 tab 用，不含账号信息页） ─────
const REPORT_STEPS = [
  { key: 'dimensions', label: '六维诊断' },
  { key: 'problems', label: '关键问题' },
  { key: 'actions', label: '行动与实验' },
  { key: 'data', label: '数据说明' },
];

function PlatformDot({ p }: { p: string }) {
  const info = PLATFORM_INFO[p];
  if (!info) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${info.badgeCls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${info.dot}`} />
      {info.label}
    </span>
  );
}

// ─── 主页面 ─────────────────────────────────────────────────────

export default function DiagnosisPage() {
  // 流程向导：1 选择平台 → 2 账号信息 → 3 确认报价 → 4 采集与报告
  const [step, setStep] = React.useState<1 | 2 | 3 | 4>(1);
  const [platform, setPlatform] = React.useState<DiagnosisPlatform>('xiaohongshu');

  // 输入
  const [input, setInput] = React.useState('');
  const [goal, setGoal] = React.useState('consult');
  const [audience, setAudience] = React.useState('');
  const [positioning, setPositioning] = React.useState('');
  const [product, setProduct] = React.useState('');
  const [quoteResult, setQuoteResult] = React.useState<DiagnosisQuoteResponse | null>(null);
  const [quoting, setQuoting] = React.useState(false);
  const [quoteError, setQuoteError] = React.useState('');

  // 运行
  const [selectedTier, setSelectedTier] = React.useState<string>('basic');
  const [runId, setRunId] = React.useState<number | null>(null);
  const [run, setRun] = React.useState<DiagnosisRun | null>(null);
  const [report, setReport] = React.useState<any>(null);
  const [raw, setRaw] = React.useState<any>(null); // 完整原始抓取快照
  const [targetId, setTargetId] = React.useState<number | null>(null);
  const [starting, setStarting] = React.useState(false);
  const [startError, setStartError] = React.useState('');

  // 报告分页
  const [reportStep, setReportStep] = React.useState(0);
  // 结果区视图：账号与作品 / 诊断报告
  const [viewTab, setViewTab] = React.useState<'account' | 'report'>('account');

  // 历史（右栏常驻）
  const [runs, setRuns] = React.useState<DiagnosisRun[]>([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);

  // 经营补录
  const [showOutcomes, setShowOutcomes] = React.useState(false);
  const [outcomes, setOutcomes] = React.useState<any[]>([]);
  const [outcomeForm, setOutcomeForm] = React.useState<Record<string, string>>({});

  const platformCfg = PLATFORMS.find((p) => p.value === platform) || PLATFORMS[0];

  // 选择平台：重置输入/报价/报告并进入下一步（账号信息）
  const pickPlatform = (p: DiagnosisPlatform) => {
    setPlatform(p);
    setInput('');
    setQuoteResult(null);
    setReport(null);
    setRaw(null);
    setRunId(null);
    setRun(null);
    setStartError('');
    setSelectedTier('basic');
    setReportStep(0);
    setViewTab('account');
    const cfg = PLATFORMS.find((x) => x.value === p) || PLATFORMS[0];
    setGoal(cfg.goals[0]?.value || 'consult');
    setStep(2);
  };

  const loadRuns = React.useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await diagnosisMyRuns();
      setRuns(data.runs || []);
    } catch {
      setRuns([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  // 轮询运行状态
  React.useEffect(() => {
    if (!runId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const data = await diagnosisGetRun(runId);
        if (cancelled) return;
        setRun(data.run);
        if (data.target?.id) setTargetId(data.target.id);
        if (data.run.status === 'completed' || data.run.status === 'partial') {
          const rp = await diagnosisGetReport(runId);
          if (!cancelled) {
            setReport(rp.report);
            setRaw(rp.raw || null);
            setViewTab('account'); // 完成后先展示账号与作品
          }
          return; // 停止轮询
        }
      } catch {
        /* 轮询失败继续 */
      }
      setTimeout(tick, 2500);
    };
    tick();
    return () => { cancelled = true; };
  }, [runId]);

  const openRun = async (id: number) => {
    setRunId(id);
    setStep(4); // 右栏历史 → 直接进入采集与报告步骤
    setReportStep(0);
    setViewTab('account');
    try {
      const rp = await diagnosisGetReport(id);
      setReport(rp.report);
      setRaw(rp.raw || null);
      const rr = await diagnosisGetRun(id);
      setRun(rr.run);
      if (rr.target?.id) setTargetId(rr.target.id);
      if (rr.run?.platform) setPlatform(rr.run.platform as DiagnosisPlatform);
    } catch { /* ignore */ }
  };

  const handleQuote = async () => {
    if (!input.trim()) {
      setQuoteError(`请先输入${platformCfg.label}账号链接`);
      return;
    }
    setQuoting(true);
    setQuoteError('');
    setQuoteResult(null);
    setReport(null);
    setRaw(null);
    try {
      const data = await diagnosisQuote({
        platform,
        input: input.trim(),
        primary_goal: goal,
        intended_audience: audience,
        intended_positioning: positioning,
        product_offer: product,
      });
      setQuoteResult(data);
      setStep(3); // 报价成功 → 进入确认报价步骤
    } catch (err) {
      setQuoteError(err instanceof Error ? err.message : '报价失败');
    } finally {
      setQuoting(false);
    }
  };

  const handleStart = async () => {
    if (!quoteResult) return;
    setStarting(true);
    setStartError('');
    setReport(null);
    setRaw(null);
    try {
      const data = await diagnosisCreateRun({
        platform,
        input: input.trim(),
        primary_goal: goal,
        intended_audience: audience,
        intended_positioning: positioning,
        product_offer: product,
        tier: selectedTier,
        idempotency_key: crypto.randomUUID().slice(0, 12),
      });
      setRunId(data.run_id);
      setRun({ ...(run || {}), id: data.run_id, status: 'queued', credit_charged: data.credit_charged } as DiagnosisRun);
      setStep(4); // 确认开始 → 进入采集与报告步骤
      loadRuns();
    } catch (err) {
      setStartError(err instanceof Error ? err.message : '启动失败');
    } finally {
      setStarting(false);
    }
  };

  const handleRetry = async () => {
    if (!runId) return;
    try {
      await diagnosisRetryRun(runId);
      setReport(null);
      setRaw(null);
      setRun((prev) => ({ ...prev!, status: 'queued' }));
    } catch (err) {
      setStartError(err instanceof Error ? err.message : '重试失败');
    }
  };

  const loadOutcomes = async () => {
    if (targetId) {
      try {
        const od = await diagnosisGetOutcomes(targetId);
        setOutcomes(od.outcomes || []);
      } catch { /* ignore */ }
    }
  };

  const handleAddOutcome = async () => {
    if (!targetId) {
      setStartError('缺少目标ID，无法补录（请先完成一次体检）');
      return;
    }
    try {
      const payload: Record<string, any> = {};
      for (const [k, v] of Object.entries(outcomeForm)) {
        if (v !== '' && v !== null && v !== undefined) payload[k] = Number(v);
      }
      await diagnosisAddOutcome(targetId, payload);
      const od = await diagnosisGetOutcomes(targetId);
      setOutcomes(od.outcomes || []);
      setOutcomeForm({});
      setShowOutcomes(false);
    } catch (err) {
      setStartError(err instanceof Error ? err.message : '补录失败');
    }
  };

  // PDF 导出：打印视图（浏览器打印/另存为 PDF）
  const handleExportPdf = () => {
    window.print();
  };

  const progressStatus = run?.status || 'idle';

  // ─── 账号与作品视图（主 tab） ─────────────────────────────────
  const renderAccountView = () => {
    if (!report) return null;
    const profile = raw?.profile || report?.account || {};
    const contents = raw?.contents || [];
    return (
      <div className="space-y-4">
        {/* 核心诊断横幅 */}
        <Card className="border-l-4 border-l-primary">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div>
                <div className="text-sm font-medium text-muted-foreground">核心诊断</div>
                <p className="mt-1 text-lg font-medium">{report.core_diagnosis || '—'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 账号完整信息（人设页版式） */}
        <AccountInfoCard profile={profile} platform={report.platform || platform} />

        {/* 抽查作品 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> 抽查作品 <span className="text-sm font-normal text-muted-foreground">（{contents.length} 条 · 已同步到内容库）</span></CardTitle>
          </CardHeader>
          <CardContent>
            <WorksGrid contents={contents} platform={report.platform || platform} />
          </CardContent>
        </Card>
      </div>
    );
  };

  // ─── 诊断报告视图（独立 tab） ─────────────────────────────────
  const renderReportView = () => {
    if (!report) return null;
    const profile = raw?.profile || report?.account || {};
    const contents = raw?.contents || [];
    const dims = report.dimensions || {};

    // 六维平均（仅计有分维度）
    const scoredDims = Object.values(dims).filter((d: any) => typeof d?.score === 'number');
    const dimAvg = scoredDims.length
      ? Math.round(scoredDims.reduce((s: number, d: any) => s + (d.score || 0), 0) / scoredDims.length)
      : null;

    switch (REPORT_STEPS[reportStep]?.key) {
      // ── 步骤 1：六维诊断（雷达图 + 状态卡） ──
      case 'dimensions':
        return (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Activity className="h-5 w-5" /> 六维状态</CardTitle>
                <CardDescription>
                  六维评分{dimAvg != null ? ` · 综合平均 ${dimAvg}` : ''}（仅统计有数据的维度）
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 lg:grid-cols-2">
                  {/* 左：雷达图 */}
                  <div className="lg:sticky lg:top-4 lg:self-start">
                    <SixDimRadar dimensions={dims} />
                  </div>
                  {/* 右：六维说明细节 */}
                  <div className="grid gap-3 sm:grid-cols-2">
                    {Object.entries(dims).map(([k, v]) => (
                      <DimensionCard key={k} dim={v} />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {report.platform_cards && report.platform_cards.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5" /> {PLATFORM_LABEL[report.platform] || ''}专属</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {report.platform_cards.map((c: any, i: number) => (
                    <div key={i} className="rounded-lg border p-4">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">{c.title}</div>
                        <Badge variant={c.available ? 'secondary' : 'outline'}>
                          {c.available ? '已覆盖' : '数据不足'}
                        </Badge>
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground">{c.note}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* 账号信息与作品（报告中再展示一遍） */}
            <AccountInfoCard profile={profile} platform={report.platform || platform} />
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> 抽查作品 <span className="text-sm font-normal text-muted-foreground">（{contents.length} 条）</span></CardTitle>
              </CardHeader>
              <CardContent>
                <WorksGrid contents={contents} platform={report.platform || platform} />
              </CardContent>
            </Card>
          </div>
        );

      // ── 步骤 2：关键问题 ──
      case 'problems':
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> 最关键的问题</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {report.top_problems?.length ? report.top_problems.map((p: any, i: number) => (
                <div key={i} className="rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">
                      <span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">{i + 1}</span>
                      {p.title}
                    </div>
                    <Badge variant="outline">{p.confidence}</Badge>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{p.conclusion}</p>
                  {p.evidence?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {p.evidence.slice(0, 3).map((e: string, j: number) => (
                        <div key={j} className="flex items-start gap-1 text-xs text-muted-foreground">
                          <ChevronRight className="mt-0.5 h-3 w-3 shrink-0" /> {e}
                        </div>
                      ))}
                    </div>
                  )}
                  {p.recommendation && (
                    <div className="mt-2 text-sm text-emerald-600 dark:text-emerald-400">→ {p.recommendation}</div>
                  )}
                </div>
              )) : (
                <p className="text-sm text-muted-foreground">未识别到明显问题。</p>
              )}
            </CardContent>
          </Card>
        );

      // ── 步骤 3：行动与实验 ──
      case 'actions':
        return (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5" /> 应继续 / 停止 / 开始</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-3">
                <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4">
                  <div className="text-sm font-medium text-emerald-600 dark:text-emerald-400">继续</div>
                  <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                    {report.actions?.continue?.map((a: string, i: number) => <li key={i}>• {a}</li>)}
                  </ul>
                </div>
                <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-4">
                  <div className="text-sm font-medium text-red-600 dark:text-red-400">停止</div>
                  <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                    {report.actions?.stop?.map((a: string, i: number) => <li key={i}>• {a}</li>)}
                  </ul>
                </div>
                <div className="rounded-lg border border-sky-500/20 bg-sky-500/5 p-4">
                  <div className="text-sm font-medium text-sky-600 dark:text-sky-400">开始</div>
                  <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                    {report.actions?.start?.map((a: string, i: number) => <li key={i}>• {a}</li>)}
                  </ul>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><FlaskConical className="h-5 w-5" /> 两周内可验证的实验</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {report.experiments?.map((e: any, i: number) => (
                  <div key={i} className="rounded-lg border p-4">
                    <div className="font-medium">{i + 1}. {e.title}</div>
                    <div className="mt-1 flex flex-wrap gap-2 text-xs">
                      <Badge variant="secondary">观察：{e.target_metric}</Badge>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">{e.how}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        );

      // ── 步骤 4：数据说明 + 经营补录 ──
      case 'data':
      default:
        return (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Info className="h-5 w-5" /> 数据说明与置信度</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">平台：{PLATFORM_LABEL[report.platform] || report.platform}</Badge>
                  <Badge variant="outline">样本 {report.data_scope?.sample_size ?? '—'} 条</Badge>
                  <Badge variant="outline">来源：{(report.data_scope?.source || []).join(' / ')}</Badge>
                  {report.platform === 'xiaohongshu' && (
                    <Badge variant="outline">PGY {report.data_scope?.pgy_hit ? '命中' : '未覆盖'}</Badge>
                  )}
                  <Badge variant="outline">置信度：{report.confidence?.level || '—'}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">{report.data_scope?.note}</p>
                <p className="text-xs text-muted-foreground">{report.disclaimer}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> 经营结果补录</CardTitle>
                <CardDescription>补录私信、线索、成交数据后，业务承接维度才能评估</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setShowOutcomes(!showOutcomes); loadOutcomes(); }}>
                    <ClipboardList className="mr-1 h-4 w-4" /> 补录经营数据
                  </Button>
                </div>
                {showOutcomes && (
                  <div className="mt-4 grid gap-3 rounded-lg border p-4 sm:grid-cols-2 lg:grid-cols-4">
                    {[
                      { k: 'dm_count', label: '私信数' },
                      { k: 'qualified_lead_count', label: '有效线索' },
                      { k: 'deal_count', label: '成交数' },
                      { k: 'revenue_cny', label: '成交金额(元)' },
                      ...(platform === 'wechat' ? [
                        { k: 'wechat_contact_added_count', label: '新增微信好友' },
                        { k: 'group_join_count', label: '进群数' },
                        { k: 'official_account_follow_count', label: '公众号新增关注' },
                        { k: 'live_lead_count', label: '直播线索' },
                      ] : []),
                    ].map((f) => (
                      <div key={f.k}>
                        <Label className="text-xs">{f.label}</Label>
                        <Input
                          type="number"
                          value={outcomeForm[f.k] ?? ''}
                          onChange={(e) => setOutcomeForm({ ...outcomeForm, [f.k]: e.target.value })}
                        />
                      </div>
                    ))}
                    <Button size="sm" onClick={handleAddOutcome} className="sm:col-span-2 lg:col-span-4">保存补录</Button>
                  </div>
                )}
                {outcomes.length > 0 && (
                  <div className="mt-3 text-sm text-muted-foreground">已补录 {outcomes.length} 条记录</div>
                )}
              </CardContent>
            </Card>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">账号体检</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          选择平台，诊断账号最先断掉的环节，并给出可验证的下一轮动作
        </p>
      </div>

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        {/* ── 左：流程向导 ── */}
        <div className="min-w-0 space-y-6">
          {/* 步骤条 */}
          <div className="flex items-center gap-1 rounded-lg border bg-muted/30 px-4 py-3">
            {WIZARD_STEPS.map((s, i) => {
              const on = step === s.key;
              const done = step > s.key;
              return (
                <React.Fragment key={s.key}>
                  {i > 0 && <span className="mx-1 text-muted-foreground/50">›</span>}
                  <div className={`flex items-center gap-2 text-sm ${on ? 'font-semibold text-foreground' : done ? 'text-muted-foreground' : 'text-muted-foreground/70'}`}>
                    <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                      on ? 'bg-primary text-primary-foreground'
                        : done ? 'bg-emerald-500 text-white'
                        : 'border border-border text-muted-foreground'
                    }`}>
                      {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : s.key}
                    </span>
                    {s.label}
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {/* ── 步骤1：选择平台（三大模块卡片） ── */}
          {step === 1 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Stethoscope className="h-5 w-5 text-primary" /> 选择体检平台</CardTitle>
                <CardDescription>每个平台的数据口径与报告侧重不同，点击卡片进入下一步</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-3">
                  {PLATFORMS.map((p) => {
                    const info = PLATFORM_INFO[p.value];
                    return (
                      <button
                        key={p.value}
                        onClick={() => pickPlatform(p.value)}
                        className="group relative flex flex-col overflow-hidden rounded-xl border bg-background p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md"
                      >
                        <div className={`absolute inset-x-0 top-0 h-1 ${info.dot}`} />
                        <div className="flex items-center gap-2 text-lg font-bold">
                          <span className={`h-2.5 w-2.5 rounded-full ${info.dot}`} />
                          {p.label}
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">{p.tagline}</div>
                        <div className="mt-4 flex flex-wrap gap-1.5">
                          {p.covers.map((c) => (
                            <span key={c} className="rounded-md border bg-muted/50 px-2 py-0.5 text-[11px] text-muted-foreground">{c}</span>
                          ))}
                        </div>
                        <div className="mt-4 border-t border-dashed pt-3 text-[11px] leading-relaxed text-muted-foreground">
                          <span className="font-medium text-foreground">流程：</span>
                          {p.steps.join(' → ')}
                        </div>
                        <div className={`mt-4 rounded-lg border px-3 py-1.5 text-center text-xs font-semibold transition-colors ${info.badgeCls} group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary`}>
                          选择{p.label}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ── 步骤2：账号与目标（平台色点缀） ── */}
          {step === 2 && (
            <Card className="overflow-hidden">
              <div className={`h-1 w-full ${PLATFORM_INFO[platform]?.dot || 'bg-primary'}`} />
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ScanSearch className={`h-5 w-5 ${PLATFORM_INFO[platform]?.textCls || 'text-primary'}`} />
                  {platformCfg.label} · 账号与目标
                </CardTitle>
                <CardDescription>{platformCfg.hint}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{platformCfg.label}账号</Label>
                  <Input
                    placeholder={platformCfg.hint}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label>主要目标</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={goal}
                      onChange={(e) => setGoal(e.target.value)}
                    >
                      {platformCfg.goals.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>期望人群（可选）</Label>
                    <Input placeholder="如：成都准备装修的家庭" value={audience} onChange={(e) => setAudience(e.target.value)} />
                  </div>
                  <div>
                    <Label>期望定位（可选）</Label>
                    <Input placeholder="如：成都家居改造与好物博主" value={positioning} onChange={(e) => setPositioning(e.target.value)} />
                  </div>
                  <div>
                    <Label>产品/服务（可选）</Label>
                    <Input placeholder="如：全屋软装设计咨询" value={product} onChange={(e) => setProduct(e.target.value)} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => setStep(1)}>返回上一步</Button>
                  <Button onClick={handleQuote} disabled={quoting}>
                    {quoting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                    下一步：查看报价
                  </Button>
                  {quoteError && <span className="text-sm text-red-600 dark:text-red-400">{quoteError}</span>}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ── 步骤3：版本与报价 ── */}
          {step === 3 && quoteResult && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><FlaskConical className="h-5 w-5" /> 选择版本</CardTitle>
                <CardDescription>{quoteResult.note}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className={`grid gap-4 ${quoteResult.tiers.length >= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                  {quoteResult.tiers.map((t) => {
                    const meta = TIER_META[t.tier] || TIER_META.basic;
                    const selected = selectedTier === t.tier;
                    return (
                      <button
                        key={t.tier}
                        onClick={() => setSelectedTier(t.tier)}
                        className={`rounded-xl border p-4 text-left transition-all ${
                          selected
                            ? `${meta.badge} shadow-sm`
                            : 'border-border bg-background hover:border-muted-foreground/40'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`text-base font-semibold ${meta.color}`}>{t.name}</span>
                          {selected && <CheckCircle2 className={`h-5 w-5 ${meta.color}`} />}
                        </div>
                        <div className={`mt-3 text-2xl font-bold ${selected ? meta.color : ''}`}>
                          {t.credit_cost} <span className="text-sm font-normal text-muted-foreground">积分</span>
                        </div>
                        <div className="text-xs text-muted-foreground">≈ ¥{t.price_cny.toFixed(1)}</div>
                        <div className="mt-2 text-xs text-muted-foreground">{t.description}</div>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <Button variant="outline" onClick={() => setStep(2)}>返回上一步</Button>
                  <Button onClick={handleStart} disabled={starting}>
                    {starting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Activity className="mr-2 h-4 w-4" />}
                    确认并开始体检（扣 {quoteResult.tiers.find((t) => t.tier === selectedTier)?.credit_cost} 积分）
                  </Button>
                  {startError && <span className="text-sm text-red-600 dark:text-red-400">{startError}</span>}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ── 步骤4：采集进度 + 结果区 ── */}
          {step === 4 && (
            <>
              {run && !['completed', 'partial', 'settled'].includes(progressStatus) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><Clock className="h-5 w-5" /> 采集进度</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-3 flex items-center gap-2">
                      <StatusBadge status={progressStatus} />
                      <span className="text-sm text-muted-foreground">{run.progress?.message || '正在准备...'}</span>
                    </div>
                    <div className="space-y-2">
                      {platformCfg.steps.map((s, i) => {
                        const cur = run.progress?.step || '';
                        const done = progressStatus === 'completed'
                          || (cur && platformCfg.steps.indexOf(cur) > i)
                          || (cur === '作品' && i <= 1)
                          || (cur === '评论' && i <= 2)
                          || (cur === '分析' && i <= platformCfg.steps.length - 2);
                        const active = cur === s;
                        return (
                          <div key={s} className="flex items-center gap-2 text-sm">
                            {done ? <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                              : active ? <Loader2 className="h-4 w-4 animate-spin text-sky-500" />
                              : <div className="h-4 w-4 rounded-full border border-muted" />}
                            <span className={active ? 'font-medium' : 'text-muted-foreground'}>{s}</span>
                          </div>
                        );
                      })}
                    </div>
                    {progressStatus === 'failed' && (
                      <div className="mt-4 flex items-center gap-2 rounded-md bg-red-500/10 p-3 text-sm text-red-600 dark:text-red-400">
                        <AlertTriangle className="h-4 w-4" />
                        {run.error_summary || '体检失败'}
                        <Button variant="outline" size="sm" onClick={handleRetry} className="ml-auto">
                          <RefreshCw className="mr-1 h-3 w-3" /> 重试
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {report && (
                <div id="diagnosis-report" className="space-y-4 print:space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-2 print:hidden">
                    <div className="flex gap-1 rounded-lg border bg-muted/30 p-0.5">
                      <Button variant={viewTab === 'account' ? 'default' : 'ghost'} size="sm" onClick={() => setViewTab('account')}>
                        账号与作品
                      </Button>
                      <Button variant={viewTab === 'report' ? 'default' : 'ghost'} size="sm" onClick={() => { setViewTab('report'); setReportStep(0); }}>
                        诊断报告
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{formatDate(run?.completed_at || run?.created_at)}</span>
                      <Button variant="outline" size="sm" onClick={handleExportPdf}>
                        <Download className="mr-1 h-4 w-4" /> 下载 PDF
                      </Button>
                    </div>
                  </div>

                  {viewTab === 'account' && (
                    <div className="print:block">{renderAccountView()}</div>
                  )}

                  {viewTab === 'report' && (
                    <>
                      <div className="print:hidden">
                        <div className="flex items-center justify-between">
                          {REPORT_STEPS.map((s, i) => (
                            <button
                              key={s.key}
                              onClick={() => setReportStep(i)}
                              className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                                i === reportStep ? 'text-primary' : i < reportStep ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                              }`}
                            >
                              <span className={`flex h-5 w-5 items-center justify-center rounded-full border text-[10px] ${
                                i === reportStep ? 'border-primary bg-primary text-primary-foreground'
                                  : i < reportStep ? 'border-emerald-500 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                                  : 'border-border text-muted-foreground'
                              }`}>
                                {i < reportStep ? <CheckCircle2 className="h-3 w-3" /> : i + 1}
                              </span>
                              <span className="hidden sm:inline">{s.label}</span>
                            </button>
                          ))}
                        </div>
                        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full bg-primary transition-all duration-300"
                            style={{ width: `${((reportStep + 1) / REPORT_STEPS.length) * 100}%` }}
                          />
                        </div>
                      </div>

                      <div className="print:block">{renderReportView()}</div>

                      <div className="flex items-center justify-between border-t pt-4 print:hidden">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={reportStep <= 0}
                          onClick={() => setReportStep((s) => Math.max(0, s - 1))}
                        >
                          <ChevronLeft className="mr-1 h-4 w-4" /> 上一页
                        </Button>
                        <span className="text-xs text-muted-foreground">
                          {reportStep + 1} / {REPORT_STEPS.length}
                        </span>
                        <Button
                          size="sm"
                          disabled={reportStep >= REPORT_STEPS.length - 1}
                          onClick={() => setReportStep((s) => Math.min(REPORT_STEPS.length - 1, s + 1))}
                        >
                          下一页 <ChevronRight className="ml-1 h-4 w-4" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── 右：历史报告常驻 ── */}
        <div className="xl:sticky xl:top-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Clock className="h-5 w-5" /> 历史报告</CardTitle>
              <CardDescription>共 {runs.length} 份 · 点击查看详情</CardDescription>
            </CardHeader>
            <CardContent className="max-h-[calc(100vh-220px)] overflow-y-auto pr-1">
              {historyLoading ? (
                <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>
              ) : runs.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">暂无体检记录，去创建第一份体检吧。</p>
              ) : (
                <div className="space-y-2">
                  {runs.map((r) => (
                    <button key={r.id} onClick={() => openRun(r.id)} className="flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors hover:bg-accent/50">
                      <div className="flex items-center gap-3">
                        <StatusBadge status={r.status} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 text-sm font-medium">
                            {r.account_name || r.id}
                            <Badge variant="outline" className="text-xs">{PLATFORM_LABEL[r.platform] || r.platform}</Badge>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {TIER_META[r.tier] && <span className={TIER_META[r.tier].color}>{ { basic: '基础', enhanced: '增强', deep: '深度' }[r.tier] || r.tier }</span>}
                            <span className="mx-1">·</span>
                            {formatDate(r.created_at)}
                            {r.platform === 'xiaohongshu' && (
                              <><span className="mx-1">·</span>{r.pgy_available ? 'PGY已命中' : 'PGY未覆盖'}</>
                            )}
                          </div>
                          {r.core_diagnosis && <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{r.core_diagnosis}</div>}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}